import { faCheck } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import { useEffect, useRef, useState } from 'react';

import FilterDropdown from '../FilterDropdown';
import FilterPill from '../FilterPill';
import {
  ALL_FILL_RATES,
  buildMultiChipLabel,
  deriveFillStatus,
  FILL_RATE_LABELS,
  FillRateValue,
} from '../helpers';

import { FillStatusEnum } from '@/types/graphql';

type Props = {
  value: FillStatusEnum | undefined;
  onApply: (next: FillStatusEnum) => void;
  onClear: () => void;
};

/** Map the single FillStatusEnum back into the multi-checkbox set. */
const toSet = (value: FillStatusEnum | undefined): Set<FillRateValue> => {
  if (!value || value === FillStatusEnum.ALL) return new Set(ALL_FILL_RATES);
  if (value === FillStatusEnum.FILLED)
    return new Set<FillRateValue>(['FILLED']);
  return new Set<FillRateValue>(['UNFILLED']);
};

const FillRatePill = ({ value, onApply, onClear }: Props) => {
  const wrapRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [staged, setStaged] = useState<Set<FillRateValue>>(() => toSet(value));

  useEffect(() => {
    if (open) setStaged(toSet(value));
  }, [open, value]);

  const allSelected = staged.size === ALL_FILL_RATES.length;

  const toggleItem = (v: FillRateValue) => {
    setStaged((prev) => {
      const next = new Set(prev);
      if (next.has(v)) next.delete(v);
      else next.add(v);
      return next;
    });
  };

  const toggleAll = () => {
    setStaged(allSelected ? new Set() : new Set(ALL_FILL_RATES));
  };

  const apply = () => {
    onApply(deriveFillStatus(staged));
    setOpen(false);
  };

  const isActive = value !== undefined && value !== FillStatusEnum.ALL;
  const appliedSet = toSet(value);
  const selectedLabels = ALL_FILL_RATES.filter((v) => appliedSet.has(v)).map(
    (v) => FILL_RATE_LABELS[v],
  );
  const { label, more } =
    isActive && appliedSet.size < ALL_FILL_RATES.length
      ? buildMultiChipLabel(selectedLabels)
      : { label: '', more: '' };

  return (
    <div className="pill-wrap" ref={wrapRef}>
      <FilterPill
        placeholder="All fill rate"
        chipLabel={isActive ? label : undefined}
        chipMore={isActive ? more : undefined}
        open={open}
        onToggle={() => setOpen((v) => !v)}
        onClear={isActive ? onClear : undefined}
        a11yLabel="Filter by fill rate"
      />
      <FilterDropdown
        open={open}
        onClose={() => setOpen(false)}
        onApply={apply}
        anchorRef={wrapRef}
        panelStyle={{ minWidth: 180 }}
      >
        <div
          className="dropdown-option"
          role="checkbox"
          aria-checked={allSelected}
          onClick={toggleAll}
        >
          <span
            className={classNames('dropdown-check', { checked: allSelected })}
          >
            {allSelected ? <FontAwesomeIcon icon={faCheck} fixedWidth /> : null}
          </span>
          <span>{allSelected ? 'Unselect All' : 'Select All'}</span>
        </div>
        {ALL_FILL_RATES.map((v) => {
          const checked = staged.has(v);
          return (
            <div
              key={v}
              className="dropdown-option"
              role="checkbox"
              aria-checked={checked}
              onClick={() => toggleItem(v)}
            >
              <span className={classNames('dropdown-check', { checked })}>
                {checked ? <FontAwesomeIcon icon={faCheck} fixedWidth /> : null}
              </span>
              {FILL_RATE_LABELS[v]}
            </div>
          );
        })}
      </FilterDropdown>
    </div>
  );
};

export default FillRatePill;
